import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { EMPTY, catchError } from 'rxjs';

import { AuthFeatureService } from '@/core/auth/services/auth.service';
import { VerifiedLessonCodeView } from '@/core/auth/services/lesson-code-mappers/lesson-code-mappers.types';
import { AuthStore } from '@/core/auth/stores/auth.store';
import { ApiError } from '@/core/http/problem-details/problem-details';
import { ProblemType } from '@/core/http/problem-types';
import { SerieTheme } from '@/core/layout/serie-theme';
import { ToastService } from '@/core/toast/toast.service';
import {
  LESSON_START_INTENT_STATE_KEY,
  LessonStartIntent,
} from '@/shared/content/lesson-start-intent/lesson-start-intent';
import { DEFAULT_GRADE_SLUG } from '@/shared/utils/grade/grade';

export type CodeLoginStep = 'code' | 'resume' | 'identify';

export type CodeLoginAs = LessonStartIntent | 'resume';

const CODE_PATTERN = /^\d{6}$/;

@Injectable()
export class CodeLoginStore {
  private readonly authService = inject(AuthFeatureService);
  private readonly authStore = inject(AuthStore);
  private readonly serieTheme = inject(SerieTheme);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private readonly stepSignal = signal<CodeLoginStep>('code');
  private readonly codeSignal = signal('');
  private readonly isVerifyingSignal = signal(false);
  private readonly codeErrorSignal = signal<string | null>(null);
  private readonly verifiedSignal = signal<VerifiedLessonCodeView | null>(null);
  private readonly selectedStudentIdSignal = signal<number | null>(null);
  private readonly selectedGroupIdSignal = signal<number | null>(null);
  private readonly loggingInAsSignal = signal<CodeLoginAs | null>(null);

  readonly step = this.stepSignal.asReadonly();
  readonly code = this.codeSignal.asReadonly();
  readonly isVerifying = this.isVerifyingSignal.asReadonly();
  readonly codeError = this.codeErrorSignal.asReadonly();
  readonly verified = this.verifiedSignal.asReadonly();
  readonly selectedStudentId = this.selectedStudentIdSignal.asReadonly();
  readonly selectedGroupId = this.selectedGroupIdSignal.asReadonly();
  readonly loggingInAs = this.loggingInAsSignal.asReadonly();
  readonly isLoggingIn = computed(() => this.loggingInAsSignal() !== null);

  readonly canContinue = computed(
    () => CODE_PATTERN.test(this.codeSignal()) && !this.isVerifyingSignal(),
  );
  readonly canLogin = computed(
    () => this.selectedStudentIdSignal() !== null && !this.isLoggingIn(),
  );
  readonly students = computed(() => this.verifiedSignal()?.students ?? []);
  readonly hasStudents = computed(() => this.students().length > 0);
  readonly groups = computed(() => this.verifiedSignal()?.groups ?? []);
  readonly hasGroups = computed(() => this.groups().length > 0);
  readonly selectedGroup = computed(
    () => this.groups().find((group) => group.id === this.selectedGroupIdSignal()) ?? null,
  );
  readonly canContinueResume = computed(
    () => this.selectedGroupIdSignal() !== null && !this.isLoggingIn(),
  );
  readonly identifyStudents = computed(() =>
    this.students().filter((student) => !student.unavailable),
  );
  readonly hasIdentifyStudents = computed(() => this.identifyStudents().length > 0);
  readonly lessonNumber = computed(() => this.verifiedSignal()?.lessonNumber ?? null);

  enter(): void {
    this.serieTheme.use(null);
    this.stepSignal.set('code');
    this.codeSignal.set('');
    this.isVerifyingSignal.set(false);
    this.codeErrorSignal.set(null);
    this.verifiedSignal.set(null);
    this.selectedStudentIdSignal.set(null);
    this.selectedGroupIdSignal.set(null);
    this.loggingInAsSignal.set(null);
  }

  setCode(value: string): void {
    this.codeSignal.set(value);
    this.codeErrorSignal.set(null);
  }

  verify(): void {
    if (!this.canContinue()) return;

    this.isVerifyingSignal.set(true);
    this.authService
      .verifyLessonCode(this.codeSignal())
      .pipe(
        catchError((err: ApiError) => {
          this.isVerifyingSignal.set(false);
          if (err.screenReplaced) return EMPTY;
          if (err.type === ProblemType.sessionCodeInvalid) {
            this.codeErrorSignal.set(err.detail);
            return EMPTY;
          }
          this.showError(err);
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((verified) => {
        this.isVerifyingSignal.set(false);
        this.verifiedSignal.set(verified);
        this.selectedStudentIdSignal.set(null);
        this.selectedGroupIdSignal.set(null);
        this.serieTheme.use(verified.serieSlug);
        this.stepSignal.set(verified.groups.length > 0 ? 'resume' : 'identify');
      });
  }

  back(): void {
    if (this.isVerifyingSignal() || this.isLoggingIn()) return;
    if (this.stepSignal() === 'identify' && this.hasGroups()) {
      this.selectedStudentIdSignal.set(null);
      this.stepSignal.set('resume');
      return;
    }
    if (this.stepSignal() !== 'code') {
      this.returnToCode();
      return;
    }
    void this.router.navigateByUrl('/login');
  }

  selectStudent(id: number | null): void {
    this.selectedStudentIdSignal.set(id);
  }

  selectGroup(id: number): void {
    if (this.isLoggingIn()) return;
    this.selectedGroupIdSignal.update((selected) => (selected === id ? null : id));
  }

  firstAccess(): void {
    if (this.isLoggingIn()) return;
    this.selectedStudentIdSignal.set(null);
    this.stepSignal.set('identify');
  }

  continueLesson(): void {
    const group = this.selectedGroup();
    if (!this.canContinueResume() || !group) return;
    this.loginAs(group.organizer.id, 'resume');
  }

  login(intent: CodeLoginAs): void {
    const studentId = this.selectedStudentIdSignal();
    if (!this.canLogin() || studentId === null) return;
    this.loginAs(studentId, intent);
  }

  private loginAs(studentId: number, intent: CodeLoginAs): void {
    const verified = this.verifiedSignal();
    if (!verified) return;

    this.loggingInAsSignal.set(intent);
    this.authService
      .loginByCode(this.codeSignal(), studentId)
      .pipe(
        catchError((err: ApiError) => {
          this.loggingInAsSignal.set(null);
          if (err.screenReplaced) return EMPTY;
          if (err.type === ProblemType.sessionCodeInvalid) {
            this.returnToCode();
            this.codeErrorSignal.set(err.detail);
            return EMPTY;
          }
          if (err.type === ProblemType.sessionCodeUserNotInClass) {
            this.selectedStudentIdSignal.set(null);
            if (intent === 'resume') this.selectedGroupIdSignal.set(null);
          }
          this.showError(err);
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.enterLesson(verified, intent));
  }

  private returnToCode(): void {
    this.stepSignal.set('code');
    this.codeErrorSignal.set(null);
    this.verifiedSignal.set(null);
    this.selectedStudentIdSignal.set(null);
    this.selectedGroupIdSignal.set(null);
    this.serieTheme.use(null);
  }

  private enterLesson(verified: VerifiedLessonCodeView, intent: CodeLoginAs): void {
    const grade = verified.serieSlug ?? this.authStore.gradeSlug() ?? DEFAULT_GRADE_SLUG;
    if (verified.trailSlug === null) {
      void this.router.navigate(['/', grade, 'aulas'], {
        queryParams: { aula: verified.lessonSlug },
        replaceUrl: true,
      });
      return;
    }
    void this.router.navigateByUrl(
      `/${grade}/${verified.trailSlug}/${verified.lessonSlug}/iniciar`,
      intent === 'resume'
        ? { replaceUrl: true }
        : { replaceUrl: true, state: { [LESSON_START_INTENT_STATE_KEY]: intent } },
    );
  }

  private showError(err: ApiError): void {
    this.toastService.show('danger', err.detail);
  }
}
